import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { MessageSquare, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails } from '../index'

interface ChatsTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
  from?: string
}

interface OrgSession {
  sessionToken: string
  userEmail: string
  messages: { fold_name: string; messages: { role: string; content: string }[] }[]
  startTime: string | null
  endTime: string | null
  createdAt: string
}

const GET_ORGANISATION_SESSIONS = gql`
  query getOrganisationSessions($organisationId: Int!, $limit: Int, $offset: Int, $search: String) {
    getOrganisationSessions(organisationId: $organisationId, limit: $limit, offset: $offset, search: $search) {
      sessionToken
      userEmail
      messages {
        fold_name
        messages {
          role
          content
        }
      }
      startTime
      endTime
      createdAt
    }
  }
`

const PAGE_SIZE = 10

const ChatsTab: React.FC<ChatsTabProps> = ({ organisation, loading, from }) => {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<OrgSession[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchSessions = useCallback(async (orgId: number, searchValue: string, currentOffset: number, append: boolean) => {
    try {
      setLoadingSessions(true)
      const response = await authorizedGraphQLRequest<{
        getOrganisationSessions: OrgSession[]
      }>(GET_ORGANISATION_SESSIONS, {
        organisationId: orgId,
        limit: PAGE_SIZE,
        offset: currentOffset,
        search: searchValue || undefined,
      })
      const results = response.getOrganisationSessions || []
      setSessions((prev) => (append ? [...prev, ...results] : results))
      setHasMore(results.length === PAGE_SIZE)
    } catch (error) {
      console.error('Failed to fetch organisation sessions:', error)
    } finally {
      setLoadingSessions(false)
    }
  }, [])

  // Reset and fetch when organisation changes
  useEffect(() => {
    if (organisation?.id) {
      setOffset(0)
      setSearch('')
      setSessions([])
      setHasMore(true)
      fetchSessions(organisation.id, '', 0, false)
    }
  }, [organisation?.id, fetchSessions])

  const handleSearchChange = (value: string) => {
    setSearch(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      if (!organisation?.id) return

      if (value.length === 0) {
        // Clear search - refetch all
        setOffset(0)
        setSessions([])
        setHasMore(true)
        fetchSessions(organisation.id, '', 0, false)
      } else if (value.length >= 3) {
        // Trigger search
        setOffset(0)
        setSessions([])
        setHasMore(true)
        fetchSessions(organisation.id, value, 0, false)
      }
    }, 500)
  }

  const handleLoadMore = () => {
    if (!organisation?.id || loadingSessions) return
    const newOffset = offset + PAGE_SIZE
    setOffset(newOffset)
    fetchSessions(organisation.id, search, newOffset, true)
  }

  const handleSessionClick = (session: OrgSession) => {
    const params = new URLSearchParams({ admin: 'true' })
    if (organisation?.id) params.set('org', String(organisation.id))
    if (from) params.set('from', from)
    navigate(`/chat/${session.sessionToken}?${params.toString()}`)
  }

  const getMessageCount = (session: OrgSession): number => {
    return session.messages.reduce((total, fold) => total + (fold.messages?.length || 0), 0)
  }

  const formatDuration = (session: OrgSession): string | null => {
    if (!session.startTime || !session.endTime) return null
    const start = new Date(session.startTime).getTime()
    const end = new Date(session.endTime).getTime()
    const diffMs = end - start
    if (diffMs <= 0) return null
    const mins = Math.floor(diffMs / 60000)
    const secs = Math.floor((diffMs % 60000) / 1000)
    return `${mins}m ${secs}s`
  }

  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by email (@) or session token"
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {search.length > 0 && search.length < 3 && <p className="text-xs text-gray-400 mt-1">Type at least 3 characters to search</p>}
      </div>

      {/* Sessions List */}
      {loadingSessions && sessions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No chat sessions found</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session) => (
                  <tr key={session.sessionToken} onClick={() => handleSessionClick(session)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-gray-900">{session.userEmail || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(session.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getMessageCount(session)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDuration(session) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* View More */}
          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleLoadMore} disabled={loadingSessions}>
                {loadingSessions ? 'Loading...' : 'View More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ChatsTab
